import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Users, 
  Award, 
  Shield, 
  Heart, 
  Zap, 
  Globe, 
  Star,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Linkedin
} from 'lucide-react';
import ceoImage from '../assets/images/ceo.jpeg';
import ctoImage from '../assets/images/cto.jpeg';
import aboutus from '../assets/images/aboutus.png';
import AnimatedCounter from '../components/AnimatedCounter';
import { useCategories } from '../hooks/useFirestoreServices';

export default function AboutPage() {
  const { categories, loading: categoriesLoading } = useCategories();
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white py-12 md:py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 md:top-20 left-10 md:left-20 w-16 md:w-32 h-16 md:h-32 bg-white opacity-10 rounded-full"></div>
          <div className="absolute bottom-10 md:bottom-20 right-10 md:right-20 w-24 md:w-48 h-24 md:h-48 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-20 md:top-40 right-20 md:right-40 w-10 md:w-20 h-10 md:h-20 bg-white opacity-15 rounded-full"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-white to-primary-100 bg-clip-text text-transparent">
              About RAPSAPP
            </h1>
            <p className="text-base md:text-xl lg:text-2xl text-primary-100 max-w-4xl mx-auto leading-relaxed px-4">
              Transforming how you experience home services with innovation, reliability, and trust. 
              Your satisfaction is our mission.
            </p>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row justify-center items-center gap-3 md:gap-6">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-400" />
                <span className="text-sm md:text-base lg:text-lg">Verified Professionals</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 md:h-6 md:w-6 text-primary-400" />
                <span className="text-sm md:text-base lg:text-lg">100% Guaranteed</span>
              </div>
              <div className="flex items-center space-x-2">
                <Heart className="h-5 w-5 md:h-6 md:w-6 text-red-400" />
                <span className="text-sm md:text-base lg:text-lg">Customer First</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Story Section */}
      <div className="py-10 md:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <motion.div {...fadeInUp}>
                <span className="text-primary-600 font-semibold text-sm md:text-base lg:text-lg">Our Journey</span>
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6 lg:mb-8 leading-tight mt-2">
                  From Vision to <span className="text-primary-600">Reality</span>
                </h2>
                <div className="space-y-4 md:space-y-6">
                  <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed text-justify">
                    Founded in 2024, RAPSAPP began with a simple yet powerful vision: to make professional 
                    home services accessible to everyone. We noticed the gap between skilled service providers 
                    and customers who needed their expertise, and decided to bridge it with cutting-edge 
                    technology and unwavering trust.
                  </p>
                  <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed text-justify">
                    What started as a small team with big dreams has now grown into the most trusted home 
                    services platform in Bilaspur, Himachal Pradesh and surrounding areas. We've served over 5,000 happy 
                    customers and work with 1000+ verified professionals who share our commitment to excellence.
                  </p>
                  <div className="flex flex-wrap gap-2 md:gap-4 pt-2 md:pt-4">
                    <div className="flex items-center space-x-1.5 md:space-x-2 bg-primary-50 px-3 md:px-4 py-1.5 md:py-2 rounded-full">
                      <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-primary-600" />
                      <span className="text-xs md:text-sm text-primary-700 font-medium">Quality Assured</span>
                    </div>
                    <div className="flex items-center space-x-1.5 md:space-x-2 bg-green-50 px-3 md:px-4 py-1.5 md:py-2 rounded-full">
                      <Shield className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                      <span className="text-xs md:text-sm text-green-700 font-medium">Fully Insured</span>
                    </div>
                    <div className="flex items-center space-x-1.5 md:space-x-2 bg-purple-50 px-3 md:px-4 py-1.5 md:py-2 rounded-full">
                      <Heart className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                      <span className="text-xs md:text-sm text-purple-700 font-medium">Customer Centric</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            <motion.div 
              className="relative mt-8 lg:mt-0"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <img
                  src={aboutus}
                  alt="Professional team working"
                  className="rounded-xl md:rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-4 md:-bottom-6 -left-3 md:-left-6 bg-white p-3 md:p-6 rounded-lg md:rounded-xl shadow-xl">
                  <div className="flex items-center space-x-2 md:space-x-4">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center text-white text-xs font-bold">
                        <Users className="w-4 h-4 md:w-5 md:h-5" />
                      </div>
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white bg-gradient-to-br from-secondary-600 to-secondary-700 flex items-center justify-center text-white text-xs font-bold">
                        <Award className="w-4 h-4 md:w-5 md:h-5" />
                      </div>
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center text-white text-xs font-bold">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                      </div>
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white bg-primary-600 flex items-center justify-center text-white text-xs md:text-sm font-bold">
                        +50
                      </div>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-gray-500">Our Team</p>
                      <p className="text-sm md:text-lg font-bold text-gray-900">10+ Experts</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="py-10 md:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-8 md:mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-6">
              Our Mission & Vision
            </h2>
            <p className="text-sm md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Driving towards a future where quality home services are just a click away
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
            <motion.div 
              className="bg-white p-5 md:p-6 lg:p-8 rounded-xl md:rounded-2xl shadow-xl"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-center mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Star className="h-6 w-6 md:h-8 md:w-8 text-primary-600" />
                </div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-sm md:text-base lg:text-lg text-gray-600 text-center leading-relaxed">
                To revolutionize the home services industry by connecting customers with 
                verified professionals, ensuring quality, convenience, and trust in every 
                service interaction. We strive to make professional home services accessible, 
                affordable, and reliable for everyone.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-5 md:p-6 lg:p-8 rounded-xl md:rounded-2xl shadow-xl"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-center mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Zap className="h-6 w-6 md:h-8 md:w-8 text-secondary-600" />
                </div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-sm md:text-base lg:text-lg text-gray-600 text-center leading-relaxed">
                To become the leading home services platform across India, where every 
                household can access world-class services with complete peace of mind. 
                We envision a future where technology empowers both service providers 
                and customers to achieve their best outcomes.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-10 md:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-8 md:mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-6">Our Core Values</h2>
            <p className="text-sm md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              The fundamental principles that drive our mission and shape our culture
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            <motion.div 
              className="text-center group hover:bg-primary-50 p-4 md:p-5 lg:p-6 rounded-xl md:rounded-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5 lg:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-7 w-7 md:h-8 md:w-8 lg:h-10 lg:w-10 text-white" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-2 md:mb-3 lg:mb-4">Trust & Security</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                We build lasting trust through transparency, verified professionals, background checks, and guaranteed service quality that you can rely on.
              </p>
            </motion.div>
            
            <motion.div 
              className="text-center group hover:bg-secondary-50 p-4 md:p-5 lg:p-6 rounded-xl md:rounded-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5 lg:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Zap className="h-7 w-7 md:h-8 md:w-8 lg:h-10 lg:w-10 text-white" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-2 md:mb-3 lg:mb-4">Innovation</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                We continuously innovate with cutting-edge technology to make home services more accessible, convenient, and efficient for everyone.
              </p>
            </motion.div>
            
            <motion.div 
              className="text-center group hover:bg-green-50 p-4 md:p-5 lg:p-6 rounded-xl md:rounded-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-gradient-to-br from-green-500 to-green-600 w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5 lg:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Award className="h-7 w-7 md:h-8 md:w-8 lg:h-10 lg:w-10 text-white" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-2 md:mb-3 lg:mb-4">Excellence</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                We pursue excellence in every service delivery, customer interaction, and business process to exceed expectations consistently.
              </p>
            </motion.div>

            <motion.div 
              className="text-center group hover:bg-primary-50 p-4 md:p-5 lg:p-6 rounded-xl md:rounded-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5 lg:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="h-7 w-7 md:h-8 md:w-8 lg:h-10 lg:w-10 text-white" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-2 md:mb-3 lg:mb-4">Community</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                We believe in building strong communities by connecting people, supporting local professionals, and contributing to economic growth.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-10 md:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-8 md:mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-6">Meet Our Leadership</h2>
            <p className="text-sm md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              The passionate team behind RAPSAPP's success and innovation
            </p>
          </motion.div>

          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl">
            <motion.div 
              className="bg-white p-5 md:p-6 lg:p-8 rounded-xl md:rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="relative mb-4 md:mb-6">
                <img
                  src={ceoImage}
                  alt="Dr. Prem Pankaj - CEO & Founder"
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full mx-auto mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute -bottom-1 md:-bottom-2 left-1/2 transform -translate-x-1/2">
                  <div className="bg-primary-600 text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-medium">
                    Founder & CEO
                  </div>
                </div>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Dr. Prem Pankaj</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 mb-3 md:mb-4">
                A physicist turned entrepreneur, Dr. Prem Pankaj blends scientific insight with visionary leadership. At RapsApp, he leads with purpose—to simplify lives through smart, reliable, and technology-driven doorstep services.
              </p>
              <div className="flex justify-center space-x-3">
                <a 
                  href="https://www.linkedin.com/in/dr-prem-pankaj-43408815b/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
                <a 
                  href="mailto:rapsappservice@gmail.com"
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <Mail className="h-4 w-4" />
                </a>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white p-5 md:p-6 lg:p-8 rounded-xl md:rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative mb-4 md:mb-6">
                <img
                  src={ctoImage}
                  alt="Ayush Thakur - CTO"
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full mx-auto mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute -bottom-1 md:-bottom-2 left-1/2 transform -translate-x-1/2">
                  <div className="bg-secondary-600 text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-medium">
                    CTO & Co-Founder
                  </div>
                </div>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Ayush Thakur</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 mb-3 md:mb-4">
                Innovator at heart and researcher by pursuit, Ayush bridges science and technology to build secure, intelligent, and human-centered solutions. At RapsApp, he leads with a vision to simplify everyday services through innovation, trust, and digital excellence.
              </p>
              <div className="flex justify-center space-x-3">
                <a 
                  href="https://www.linkedin.com/in/ayush83461/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
                <a 
                  href="mailto:rapsappservice@gmail.com"
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <Mail className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-12 md:py-16 lg:py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white opacity-10 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white opacity-15 rounded-full"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-8 md:mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">Impact by Numbers</h2>
            <p className="text-sm md:text-lg lg:text-xl text-primary-100">Our journey in transforming home services</p>
          </motion.div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
            <motion.div 
              className="text-center p-4 md:p-6 bg-white bg-opacity-10 rounded-xl md:rounded-2xl backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 md:mb-3 bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                <AnimatedCounter value="5000+" duration={2000} />
              </div>
              <div className="text-primary-200 text-xs md:text-base lg:text-lg font-medium">Happy Customers</div>
              <p className="text-primary-300 text-[10px] md:text-sm mt-1 md:mt-2">Served with excellence</p>
            </motion.div>
            
            <motion.div 
              className="text-center p-4 md:p-6 bg-white bg-opacity-10 rounded-xl md:rounded-2xl backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 md:mb-3 bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                <AnimatedCounter value="1000+" duration={2000} />
              </div>
              <div className="text-primary-200 text-xs md:text-base lg:text-lg font-medium">Verified Professionals</div>
              <p className="text-primary-300 text-[10px] md:text-sm mt-1 md:mt-2">Background checked</p>
            </motion.div>
            
            <motion.div 
              className="text-center p-4 md:p-6 bg-white bg-opacity-10 rounded-xl md:rounded-2xl backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 md:mb-3 bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                <AnimatedCounter value={categoriesLoading ? "8+" : `${categories.length}+`} duration={2000} />
              </div>
              <div className="text-primary-200 text-xs md:text-base lg:text-lg font-medium">Service Categories</div>
              <p className="text-primary-300 text-[10px] md:text-sm mt-1 md:mt-2">Comprehensive coverage</p>
            </motion.div>
            
            <motion.div 
              className="text-center p-4 md:p-6 bg-white bg-opacity-10 rounded-xl md:rounded-2xl backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 md:mb-3 bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                <AnimatedCounter value="99.5%" duration={2000} />
              </div>
              <div className="text-primary-200 text-xs md:text-base lg:text-lg font-medium">Satisfaction Rate</div>
              <p className="text-primary-300 text-[10px] md:text-sm mt-1 md:mt-2">Customer approved</p>
            </motion.div>
          </div>

          <motion.div 
            className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="text-center">
              <div className="flex items-center justify-center mb-2 md:mb-3">
                <CheckCircle className="h-6 w-6 md:h-8 md:w-8 text-green-400 mr-2" />
                <span className="text-xl md:text-2xl font-bold">24/7</span>
              </div>
              <p className="text-primary-200 text-sm md:text-base">Customer Support</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2 md:mb-3">
                <Shield className="h-6 w-6 md:h-8 md:w-8 text-primary-400 mr-2" />
                <span className="text-xl md:text-2xl font-bold">100%</span>
              </div>
              <p className="text-primary-200 text-sm md:text-base">Service Guarantee</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2 md:mb-3">
                <Award className="h-6 w-6 md:h-8 md:w-8 text-yellow-400 mr-2" />
                <span className="text-xl md:text-2xl font-bold">1 Year</span>
              </div>
              <p className="text-primary-200 text-sm md:text-base">Industry Experience</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-10 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">Our Journey</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Key milestones that shaped RAPSAPP into the platform it is today
            </p>
          </motion.div>

          {/* Mobile Timeline - Vertical Cards */}
          <div className="md:hidden space-y-6">
            <motion.div 
              className="bg-gradient-to-br from-primary-50 to-white p-5 rounded-xl shadow-md border-l-4 border-primary-600"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center mb-3">
                <div className="w-3 h-3 bg-primary-600 rounded-full mr-3"></div>
                <span className="text-primary-600 font-bold text-sm">2024</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Foundation & Vision</h3>
              <p className="text-gray-600 text-sm">
                RAPSAPP was founded with the mission to revolutionize home services in Himachal Pradesh
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-secondary-50 to-white p-5 rounded-xl shadow-md border-l-4 border-secondary-600"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center mb-3">
                <div className="w-3 h-3 bg-secondary-600 rounded-full mr-3"></div>
                <span className="text-secondary-600 font-bold text-sm">2024</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Platform Launch</h3>
              <p className="text-gray-600 text-sm">
                Launched our digital platform connecting customers with verified service professionals
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-green-50 to-white p-5 rounded-xl shadow-md border-l-4 border-green-600"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex items-center mb-3">
                <div className="w-3 h-3 bg-green-600 rounded-full mr-3"></div>
                <span className="text-green-600 font-bold text-sm">2025</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Major Expansion</h3>
              <p className="text-gray-600 text-sm">
                Expanded services to {categoriesLoading ? '8' : categories.length}+ categories and reached 5,000+ satisfied customers
              </p>
            </motion.div>
          </div>

          {/* Desktop Timeline - Horizontal */}
          <div className="relative hidden md:block">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-gradient-to-b from-primary-300 to-secondary-300"></div>
            
            <div className="space-y-12">
              <motion.div 
                className="relative flex items-center justify-between"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-5/12 text-right pr-8">
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Foundation & Vision</h3>
                    <p className="text-gray-600 mb-3">
                      RAPSAPP was founded with the mission to revolutionize home services in Himachal Pradesh
                    </p>
                    <span className="text-primary-600 font-semibold">2024</span>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full border-4 border-white shadow-lg"></div>
                <div className="w-5/12"></div>
              </motion.div>

              <motion.div 
                className="relative flex items-center justify-between"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="w-5/12"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-secondary-600 rounded-full border-4 border-white shadow-lg"></div>
                <div className="w-5/12 text-left pl-8">
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Platform Launch</h3>
                    <p className="text-gray-600 mb-3">
                      Launched our digital platform connecting customers with verified service professionals
                    </p>
                    <span className="text-secondary-600 font-semibold">2024</span>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="relative flex items-center justify-between"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="w-5/12 text-right pr-8">
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Major Expansion</h3>
                    <p className="text-gray-600 mb-3">
                      Expanded services to {categoriesLoading ? '8' : categories.length}+ categories and reached 5,000+ satisfied customers
                    </p>
                    <span className="text-green-600 font-semibold">2025</span>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full border-4 border-white shadow-lg"></div>
                <div className="w-5/12"></div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact & CTA Section */}
      <div className="py-20 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Experience the <span className="text-primary-200">Difference?</span>
              </h2>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                Join over 5,000 satisfied customers who trust RAPSAPP for their home service needs. 
                Experience quality, reliability, and convenience like never before.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                  <span className="text-primary-100">Instant booking and confirmation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                  <span className="text-primary-100">Verified and insured professionals</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                  <span className="text-primary-100">24/7 customer support</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                  <span className="text-primary-100">100% satisfaction guarantee</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/services"
                  className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-300 inline-flex items-center justify-center text-lg group"
                >
                  Browse Services
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 inline-flex items-center justify-center text-lg"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white bg-opacity-10 p-8 rounded-2xl backdrop-blur-sm"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-center">Get in Touch</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Visit Us</h4>
                    <p className="text-primary-100">
                      H. No. 67 Second floor, Joginder Nagar, District Mandi,<br />
                      Himachal Pradesh - 175015, India<br />
                      
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Call Us</h4>
                    <p className="text-primary-100">
                      +91 7018401540<br />
                      Available 24/7
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Email Us</h4>
                    <p className="text-primary-100">
                      rapsappservice@gmail.com<br />
                      www.rapsapp.in
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
